import { useEffect, useState } from 'react'
import { NextPage } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import styled, { createGlobalStyle } from 'styled-components'
import { t } from 'localization'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { colors, fonts, media, zIndex } from 'shared/lib'
import { Raffle } from 'shared/types/raffles'
import { getRaffleDisplayStatus } from 'shared/components/Raffles/raffleUtils'
import { RaffleGrid, FilterRow, FilterPill } from 'shared/components/Raffles/RaffleGrid'
import { RaffleEmptyState } from 'shared/components/Raffles/RaffleEmptyState'
import { RaffleNotifyModal } from 'shared/components/Raffles/RaffleNotifyModal'
import { FC_STORES } from 'shared/constants/raffleStores'
import { IvarFonts } from 'shared/styles/globalFonts'
import { GetServerPageProps } from 'shared/utils/getServerSideProps'
import { getRafflesServerSideProps } from 'shared/utils/getRafflesServerSideProps'

const TP = 'pages.raffles'

type FilterType = 'all' | 'open'

type RafflesPageProps = GetServerPageProps & {
  raffles: Raffle[]
  error?: boolean
}

/* The global stylesheet (src/shared/styles/global.css) sets
   `html, body { overflow-x: hidden }`, which silently makes the
   document the scroll container and disables position:sticky on
   descendants. Override for this page so the sticky sidebar/breadcrumb
   behavior matches the prototype (same fix used on the raffle PDP).

   Also override PageLayout's default FC2_OFF_WHITE body background
   so the raffle index page renders on pure white.

   Also make the nav scroll away (position: static) to match the
   standard PDP behavior across the site. */
const StickyScrollFix = createGlobalStyle`
  html, body {
    overflow-x: visible;
    background-color: ${colors.FC2_WHITE};
  }

  /* PageLayout's outer .main-page-layout div sits behind the content
     area; flip it to white so any gaps between our grid children
     (e.g. between MobileShell and the Page wrapper) don't show the
     default FC2_OFF_WHITE. Banner/Nav/Footer paint over this. */
  .main-page-layout {
    background-color: ${colors.FC2_WHITE};
  }

  /* PageLayout__Page also needs white background for raffles */
  [class*="PageLayout__Page"] {
    background-color: ${colors.FC2_WHITE};
  }

  [class*="Navigation__ScrollContainer"] {
    position: static !important;
  }
`

const RafflesPage: NextPage<RafflesPageProps> = ({ raffles, canonicalUrl }) => {
  const [filter, setFilter] = useState<FilterType>('all')
  const [showNotifyModal, setShowNotifyModal] = useState(false)

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const filteredRaffles = filter === 'open'
    ? raffles.filter(r => getRaffleDisplayStatus(r) === 'active')
    : raffles

  useEffect(() => {
    sendTrackingEvent('DRAWING_INDEX_VIEW', {
      total_drawings_count: raffles.length,
      open_drawings_count: raffles.filter((r) => getRaffleDisplayStatus(r) === 'active').length,
    })
    // Fire once on mount with the initial server-side raffle set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleNotifyCtaClick = () => {
    sendTrackingEvent('DRAWING_NOTIFY_TAP', {
      product_template_slug: null,
      source: 'index_sidebar',
      drawing_status: 'upcoming',
    })
    setShowNotifyModal(true)
  }

  /* Reused JSX for the page description copy. Rendered inside the
     desktop sticky `Sidebar` (`<Description>`) and again at the bottom
     of the mobile flow (`<MobileDescription>`). Keep the two store
     links + connecting copy in sync. */
  const descriptionContent = (
    <>
      {t(
        `${TP}.descriptionPrefix`,
        'Purchase new releases and iconic styles from the past at or under retail price. Enter our drawings online and pick up in store at our ',
      )}
      <Link href="/storelocator/new-york" passHref legacyBehavior>
        <StoreLink>{t(`${TP}.storeNewYork`, 'New York')}</StoreLink>
      </Link>
      {', '}
      <Link href="/storelocator/los-angeles" passHref legacyBehavior>
        <StoreLink>{t(`${TP}.storeLosAngeles`, 'Los Angeles')}</StoreLink>
      </Link>
      {', '}
      <Link href="/storelocator/miami" passHref legacyBehavior>
        <StoreLink>{t(`${TP}.storeMiami`, 'Miami')}</StoreLink>
      </Link>
      {' or '}
      <Link href="/storelocator/tokyo" passHref legacyBehavior>
        <StoreLink>{t(`${TP}.storeTokyo`, 'Tokyo')}</StoreLink>
      </Link>
      {t(`${TP}.descriptionSuffix`, ' location.')}
      {t(`${TP}.officialRulesPrefix`, ' For full details, see the ')}
      <OfficialRulesLink
        href="https://support.flightclub.com/hc/en-us/articles/45796369528461-FLIGHT-CLUB-DRAWINGS-OFFICIAL-RULES"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t(`${TP}.officialRulesLinkText`, 'Official Rules')}
      </OfficialRulesLink>
      {t(`${TP}.officialRulesSuffix`, '.')}
    </>
  )

  const pageTitle = t(`${TP}.pageTitle`, 'Sneaker Release Drawings | Flight Club')
  const metaDescription = t(
    `${TP}.metaDescription`,
    'Enter the latest sneaker drawings at Flight Club – New York, Los Angeles, Miami and Tokyo',
  )

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        {raffles[0]?.mainImage?.src && (
          <meta property="og:image" content={raffles[0].mainImage.src} />
        )}
      </Head>
      <IvarFonts />
      <StickyScrollFix />

      <DesktopBreadcrumbs>
        <Link href="/" passHref legacyBehavior>
          <BreadcrumbLink>{t(`${TP}.breadcrumbHome`, 'Flight Club')}</BreadcrumbLink>
        </Link>
        <BreadcrumbSeparator>/</BreadcrumbSeparator>
        <BreadcrumbCurrent>
          {t(`${TP}.breadcrumbCurrent`, 'Drawings')}
        </BreadcrumbCurrent>
      </DesktopBreadcrumbs>

      <MobileShell>
        <MobileTitles>
          <MobileTitleLabel>{t(`${TP}.heading`, 'Flight Club')}</MobileTitleLabel>
          <MobileTitleHeading>{t(`${TP}.subheading`, 'Sneaker Drawings')}</MobileTitleHeading>
        </MobileTitles>

        <MobileHeader>
          <FilterRow>
            <FilterPill $active={filter === 'all'} onClick={() => handleFilterChange('all')}>
              {t(`${TP}.filterAll`, 'All')}
            </FilterPill>
            <FilterPill $active={filter === 'open'} onClick={() => handleFilterChange('open')}>
              {t(`${TP}.filterOpen`, 'Open')}
            </FilterPill>
          </FilterRow>
        </MobileHeader>

        <PageWrapper>
          <ContentLayout>
            <Sidebar>
              <StickyTitleBlock>
                <TitleGroup>
                  <PageLabel>{t(`${TP}.heading`, 'Flight Club')}</PageLabel>
                  <PageTitle>{t(`${TP}.subheading`, 'Sneaker Drawings')}</PageTitle>
                </TitleGroup>

                <FilterRow>
                  <FilterPill $active={filter === 'all'} onClick={() => handleFilterChange('all')}>
                    {t(`${TP}.filterAll`, 'All')}
                  </FilterPill>
                  <FilterPill $active={filter === 'open'} onClick={() => handleFilterChange('open')}>
                    {t(`${TP}.filterOpen`, 'Open')}
                  </FilterPill>
                </FilterRow>
              </StickyTitleBlock>

              <StickyBottomWrapper>
                <Description>{descriptionContent}</Description>

                <NotifyPanel data-qa="raffles-notify-panel-desktop">
                  <NotifyPanelVideo autoPlay loop muted playsInline>
                    <source src="/static/raffles/raffle-signup-loop.webm" type="video/webm" />
                  </NotifyPanelVideo>
                  <NotifyPanelContent>
                    <NotifyPanelText>
                      {t(
                        `${TP}.notifyPanelText`,
                        'Be the first to know when new drawings open.',
                      )}
                    </NotifyPanelText>
                    <NotifyPanelCta
                      onClick={handleNotifyCtaClick}
                      data-qa="raffles-notify-cta-desktop"
                    >
                      {t(`${TP}.notifyCta`, 'Notify Me')}
                    </NotifyPanelCta>
                  </NotifyPanelContent>
                </NotifyPanel>
              </StickyBottomWrapper>
            </Sidebar>

            <GridArea>
              <RaffleGrid
                raffles={filteredRaffles}
                emptyState={<RaffleEmptyState />}
              />
            </GridArea>
          </ContentLayout>

          <MobileDescription>{descriptionContent}</MobileDescription>

          <MobileNotifyPanelWrapper>
            <NotifyPanel data-qa="raffles-notify-panel">
              <NotifyPanelVideo autoPlay loop muted playsInline>
                <source src="/static/raffles/raffle-signup-loop.webm" type="video/webm" />
              </NotifyPanelVideo>
              <NotifyPanelContent>
                <NotifyPanelText>
                  {t(
                    `${TP}.notifyPanelText`,
                    'Be the first to know when new drawings open.',
                  )}
                </NotifyPanelText>
                <NotifyPanelCta
                  onClick={handleNotifyCtaClick}
                  data-qa="raffles-notify-cta"
                >
                  {t(`${TP}.notifyCta`, 'Notify Me')}
                </NotifyPanelCta>
              </NotifyPanelContent>
            </NotifyPanel>
          </MobileNotifyPanelWrapper>

          <MobileBreadcrumbs data-qa="raffles-mobile-breadcrumbs">
            <Link href="/" passHref legacyBehavior>
              <MobileBreadcrumbLink>
                {t(`${TP}.breadcrumbHome`, 'Flight Club')}
              </MobileBreadcrumbLink>
            </Link>
            <MobileSeparator>/</MobileSeparator>
            <MobileBreadcrumbCurrent>
              {t(`${TP}.breadcrumbCurrent`, 'Drawings')}
            </MobileBreadcrumbCurrent>
          </MobileBreadcrumbs>
        </PageWrapper>
      </MobileShell>

      {showNotifyModal && (
        <RaffleNotifyModal
          stores={Object.keys(FC_STORES)}
          onClose={() => setShowNotifyModal(false)}
        />
      )}
    </>
  )
}

export const getServerSideProps = getRafflesServerSideProps

export default RafflesPage

const PageWrapper = styled.div`
  width: 100%;
  background-color: ${colors.FC2_WHITE};

  ${media.biggest`
    max-width: 1840px;
    margin: 0 auto;
  `}
`

const DesktopBreadcrumbs = styled.nav`
  display: none;

  ${media.larger`
    position: static;
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 40px;
    height: 40px;
    font-size: 12px;
    overflow: hidden;
    white-space: nowrap;
  `}

  ${media.biggest`
    max-width: 1840px;
    margin: 0 auto;
  `}
`

const BreadcrumbLink = styled.a`
  ${fonts.BREADCRUMB_LINK}
  cursor: pointer;
`

const BreadcrumbSeparator = styled.span`
  ${fonts.BREADCRUMB_TEXT}
`

const BreadcrumbCurrent = styled.span`
  ${fonts.BREADCRUMB_TEXT}
  white-space: nowrap;
`

/* Mobile-only wrapper grid. On mobile, lays out children as:
     row 1: [titles]  [pills]
     row 2: [page content spanning both cols]
   So pills sit on the same horizontal line as titles on load, and
   position:sticky on MobileHeader pins them below the nav on scroll.
   On desktop, display:contents makes this wrapper transparent so the
   existing layout is unaffected. */
const MobileShell = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  background-color: ${colors.FC2_WHITE};

  > ${PageWrapper} {
    grid-column: 1 / -1;
  }

  ${media.larger`
    display: contents;
  `}
`

const MobileHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: ${zIndex.justUnderNav};
  display: flex;
  justify-content: flex-end;
  align-self: center;
  padding: 24px 24px 0 0;
  background-color: transparent;

  ${media.larger`
    display: none;
  `}
`

const MobileTitles = styled.div`
  display: block;
  padding: 20px 8px 0 24px;
  z-index: 1;
  color: ${colors.FC2_RED};
  background-color: ${colors.FC2_WHITE};

  ${media.larger`
    display: none;
  `}
`

const MobileTitleLabel = styled.div`
  ${fonts.SUBTITLE_3}
  line-height: 1.2;
  margin-bottom: 4px;
  color: ${colors.FC2_RED};
  text-transform: uppercase;
`

const MobileTitleHeading = styled.h1`
  ${fonts.IVAR_HEADLINE_LARGE}
  text-transform: uppercase;
  margin: 0;
`

/* Mobile: block layout (sidebar hidden, grid flows below titles).
   Desktop: 3-col CSS grid — sidebar in col 1, grid spans cols 2-3. */
const ContentLayout = styled.div`
  display: block;

  ${media.larger`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: start;
  `}
`

/* Desktop-only sticky left sidebar column.
   Stretches to full grid row height; StickyTitleBlock and Description
   are independently sticky (top:40px / bottom:40px) to match the layout
   pattern from RaffleDetailPage. No overflow:hidden to allow sticky
   children to stick to viewport. */
const Sidebar = styled.div`
  display: none;

  ${media.larger`
    grid-column: 1;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    padding: 40px 0 40px 40px;
  `}
`

const StickyTitleBlock = styled.div`
  position: sticky;
  top: 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 95px;
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const PageLabel = styled.span`
  ${fonts.SUBTITLE_3}
  line-height: 1.2;
  color: ${colors.FC2_RED};
  text-transform: uppercase;
`

const PageTitle = styled.h1`
  ${fonts.IVAR_HEADLINE_LARGE}
  text-transform: uppercase;
  color: ${colors.FC2_RED};
  max-width: 303px;
  margin: 0;
`

const StickyBottomWrapper = styled.div`
  position: sticky;
  bottom: 40px;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
`

const Description = styled.p`
  max-width: 300px;
  ${fonts.DESCRIPTION_TEXT}
  color: ${colors.FC2_DARK_GREY};
  cursor: default;
  margin: 0;

  &:hover {
    max-height: 300px;
  }
`

const StoreLink = styled.a`
  ${fonts.SUBTITLE_3}
  line-height: 1.4;
  color: ${colors.FC2_RED};
  text-decoration: underline;
  text-decoration-skip-ink: none;
`

const OfficialRulesLink = styled.a`
  color: inherit;
  text-decoration: underline;
  text-decoration-skip-ink: none;
`

const GridArea = styled.div`
  min-width: 0;

  ${media.larger`
    grid-column: 2 / 4;
  `}
`

const NotifyPanel = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  max-width: none;
  padding: 16px;
  border-radius: 16px;
  overflow: hidden;
  background-color: rgb(55 55 55 / 80%);

  ${media.larger`
    max-width: 300px;
  `}
`

const NotifyPanelVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
  filter: saturate(0) brightness(0.9);
`

const NotifyPanelContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
`

const NotifyPanelText = styled.p`
  ${fonts.BODY_TEXT}
  line-height: 1.3;
  color: ${colors.FC2_WHITE};
  margin: 0;
  max-width: 145px;
`

const NotifyPanelCta = styled.button`
  flex-shrink: 0;
  ${fonts.SUBTITLE_3}
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
  padding: 12px 16px;
  background: ${colors.FC2_WHITE};
  color: ${colors.FC2_BLACK};
  border: 1px solid ${colors.FC2_BLACK};
  border-radius: 2px;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease, border-color 150ms ease;

  &:hover {
    background: ${colors.FC2_BLACK};
    color: ${colors.FC2_WHITE};
  }
`

const MobileNotifyPanelWrapper = styled.div`
  margin: 24px 24px 0;

  ${media.larger`
    display: none;
  `}
`

const MobileDescription = styled.p`
  margin: 0 24px;
  max-width: 392px;
  ${fonts.DESCRIPTION_TEXT}
  color: ${colors.FC2_DARK_GREY};

  ${media.larger`
    display: none;
  `}
`

const MobileBreadcrumbs = styled.nav`
  border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  padding: 24px;
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  background: ${colors.FC2_WHITE};

  ${media.larger`
    display: none;
  `}
`

const MobileBreadcrumbLink = styled.a`
  ${fonts.BREADCRUMB_LINK}
  cursor: pointer;
`

const MobileSeparator = styled.span`
  ${fonts.BREADCRUMB_TEXT}
`

const MobileBreadcrumbCurrent = styled.span`
  ${fonts.BREADCRUMB_TEXT}
  white-space: nowrap;
`
